import React, { useEffect, useState, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import api from '../services/api';
import { formatarMoeda } from '../services/formatters';
import TooltipInfo from '../components/TooltipInfo';
import { temPermissao } from '../utils/permissoes';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const CORES = ['#1e40af', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899', '#14b8a6'];
const COR_PRIMARIA = [30, 64, 175];
const COR_CINZA = [107, 114, 128];

function moeda(v) {
  return parseFloat(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function data(v) {
  if (!v) return '—';
  const s = typeof v === 'string' ? v : v instanceof Date ? v.toISOString() : String(v);
  const [y, m, d] = s.split('T')[0].split('-');
  if (!y || !m || !d) return '—';
  return `${d}/${m}/${y}`;
}

function pct(v) {
  return `${parseFloat(v || 0).toFixed(1)}%`;
}

function dataHoje() {
  return new Date().toISOString().split('T')[0];
}

function dataHa(meses) {
  const d = new Date();
  d.setMonth(d.getMonth() - meses);
  d.setDate(1);
  return d.toISOString().split('T')[0];
}

function TooltipMoeda({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', color: '#f1f5f9', padding: '10px 14px', borderRadius: 8, fontSize: 12 }}>
      <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: '#f1f5f9' }}>{p.name}: {moeda(p.value)}</p>
      ))}
    </div>
  );
}

function TooltipPie({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', color: '#f1f5f9', padding: '10px 14px', borderRadius: 8, fontSize: 12 }}>
      <p style={{ fontWeight: 600 }}>{payload[0].name}</p>
      <p>{moeda(payload[0].value)}</p>
    </div>
  );
}

function KpiCard({ titulo, valor, sub, cor, icone }) {
  return (
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 12,
      padding: '20px 24px', flex: 1, minWidth: 140,
      borderLeft: `4px solid ${cor || '#1e40af'}`,
    }}>
      <div style={{ fontSize: 22, marginBottom: 6 }}>{icone}</div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>{titulo}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{valor}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function GraficoCard({ titulo, info, children, altura = 280 }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 12, padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <h4 style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)' }}>{titulo}</h4>
        <TooltipInfo texto={info} />
      </div>
      <div style={{ height: altura }}>{children}</div>
    </div>
  );
}

function FiltroData({ inicio, fim, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <label style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>De:</label>
        <input type="date" value={inicio} onChange={e => onChange({ inicio: e.target.value, fim })}
          style={{ fontSize: 13, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6 }} />
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <label style={{ fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Até:</label>
        <input type="date" value={fim} onChange={e => onChange({ inicio, fim: e.target.value })}
          style={{ fontSize: 13, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6 }} />
      </div>
      {[
        { label: '3 meses', fn: () => onChange({ inicio: dataHa(3), fim: dataHoje() }) },
        { label: '6 meses', fn: () => onChange({ inicio: dataHa(6), fim: dataHoje() }) },
        { label: '12 meses', fn: () => onChange({ inicio: dataHa(12), fim: dataHoje() }) },
        { label: 'Tudo', fn: () => onChange({ inicio: '2000-01-01', fim: dataHoje() }) },
      ].map(({ label, fn }) => (
        <button key={label} onClick={fn} className="btn-secondary"
          style={{ fontSize: 12, padding: '5px 12px' }}>{label}</button>
      ))}
    </div>
  );
}

function labelStatus(s) {
  const m = {
    orcamento: 'Orçamento', em_andamento: 'Em Andamento',
    em_andamento_alerta: 'Em Andamento', atrasada: 'Atrasada',
    concluida: 'Entregue', concluida_pgto_pendente: 'Entregue c/ Pgto. Pendente',
    finalizada: 'Finalizada', cancelada: 'Cancelada',
    pausada: 'Pausada', orcamento_recusado: 'Orç. Recusado',
  };
  return m[s] || s;
}

// ─── EXPORTAÇÕES PDF ──────────────────────────────────────────────────────────

function cabecalhoPDF(doc, titulo, periodo) {
  doc.setFillColor(...COR_PRIMARIA);
  doc.rect(0, 0, 210, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14); doc.setFont('helvetica', 'bold');
  doc.text('CronoObra - ' + titulo, 14, 14);
  doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  if (periodo) doc.text(`Período: ${periodo}`, 196, 14, { align: 'right' });
  doc.setTextColor(50, 50, 50);
  return 30;
}

function rodapePDF(doc) {
  const total = doc.internal.getNumberOfPages();
  const gerado = new Date().toLocaleString('pt-BR');
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    const h = doc.internal.pageSize.getHeight();
    const w = doc.internal.pageSize.getWidth();
    doc.setFontSize(7); doc.setTextColor(...COR_CINZA); doc.setFont('helvetica', 'normal');
    doc.text(`Gerado em ${gerado} · CronoObra by BRASZTECH`, 14, h - 6);
    doc.text(`Página ${i} de ${total}`, w - 14, h - 6, { align: 'right' });
  }
}

function exportarResumoFinanceiroPDF(resumo, receitasMensais, periodo) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = cabecalhoPDF(doc, 'Resumo Financeiro', periodo);

  autoTable(doc, {
    startY: y, margin: { left: 14, right: 14 },
    headStyles: { fillColor: COR_PRIMARIA, textColor: 255, fontSize: 8 },
    bodyStyles: { fontSize: 9 },
    head: [['Indicador', 'Valor']],
    body: [
      ['Total Recebido no período', moeda(resumo.total_recebido)],
      ['Total Pendente de Recebimento', moeda(resumo.total_pendente)],
      ['Total Orçado (portfólio)', moeda(resumo.total_orcado)],
      ['Total Fechado (contratos)', moeda(resumo.total_fechado)],
      ['Obras Ativas', String(resumo.obras_ativas)],
      ['Ticket Médio por Obra', moeda(resumo.ticket_medio)],
      ['Taxa de Fechamento', pct(resumo.taxa_fechamento)],
    ],
    columnStyles: { 0: { cellWidth: 120 } },
    didParseCell(data) {
      if (data.column.index === 1) data.cell.styles.halign = 'right';
    },
  });
  y = doc.lastAutoTable.finalY + 12;

  doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(...COR_PRIMARIA);
  doc.text('Receita Mensal', 14, y); y += 6;

  autoTable(doc, {
    startY: y, margin: { left: 14, right: 14 },
    headStyles: { fillColor: COR_PRIMARIA, textColor: 255, fontSize: 8 },
    bodyStyles: { fontSize: 8 },
    head: [['Mês', 'Recebido', 'Pendente']],
    body: receitasMensais.map(r => [r.mes_label, moeda(r.recebido), moeda(r.pendente)]),
    didParseCell(data) {
      if (data.column.index === 1 || data.column.index === 2) data.cell.styles.halign = 'right';
    },
  });

  rodapePDF(doc);
  doc.save(`financeiro-resumo-${new Date().toISOString().split('T')[0]}.pdf`);
}

function exportarExtratoPDF(recebimentos, periodo) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = cabecalhoPDF(doc, 'Extrato de Recebimentos', periodo);

  const total = recebimentos.reduce((acc, r) => acc + parseFloat(r.valor_recebido || 0), 0);

  autoTable(doc, {
    startY: y, margin: { left: 14, right: 14 },
    headStyles: { fillColor: COR_PRIMARIA, textColor: 255, fontSize: 8 },
    bodyStyles: { fontSize: 8 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    head: [['Data', 'Obra', 'Cliente', 'Forma', 'Valor']],
    body: recebimentos.map(r => [
      data(r.data_recebimento),
      r.nome_obra,
      r.nome_cliente,
      r.forma_pagamento,
      moeda(r.valor_recebido),
    ]),
    foot: [['', '', '', 'Total', moeda(total)]],
    footStyles: { fillColor: [243, 244, 246], fontStyle: 'bold', fontSize: 8 },
    didParseCell(data) {
      if (data.column.index === 4 && data.section !== 'head') data.cell.styles.halign = 'right';
      if (data.column.index === 4 && data.section === 'head') data.cell.styles.cellPadding = { top: 3, bottom: 3, right: 3, left: 10};
      if (data.section === 'foot' && data.column.index === 3) data.cell.styles.halign = 'right';
    },
  });

  rodapePDF(doc);
  doc.save(`financeiro-extrato-${new Date().toISOString().split('T')[0]}.pdf`);
}

function exportarObrasFinanceiroPDF(obras, periodo) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const largura = doc.internal.pageSize.getWidth();

  // cabeçalho manual com largura correta (paisagem = 297mm)
  doc.setFillColor(...COR_PRIMARIA);
  doc.rect(0, 0, largura, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14); doc.setFont('helvetica', 'bold');
  doc.text('CronoObra - Situação Financeira das Obras', 14, 14);
  doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  if (periodo) doc.text(`Período: ${periodo}`, largura - 14, 14, { align: 'right' });
  doc.setTextColor(50, 50, 50);
  let y = 30;

  autoTable(doc, {
    startY: y, margin: { left: 14, right: 14 },
    headStyles: { fillColor: COR_PRIMARIA, textColor: 255, fontSize: 7.5 },
    bodyStyles: { fontSize: 7.5 },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    head: [['Obra', 'Cliente', 'Status', 'Fechado', 'Serviços', 'Materiais', 'Recebido', 'Pendente']],
    body: obras.map(o => [
      o.nome_obra,
      o.nome_cliente,
      labelStatus(o.status_obra),
      moeda(o.valor_fechado_obra),
      moeda(o.total_servicos),
      moeda(o.total_materiais),
      moeda(o.total_recebido),
      moeda(o.total_pendente),
    ]),
    didParseCell(data) {
      if (data.column.index >= 3) data.cell.styles.halign = 'right';
    },
  });

  rodapePDF(doc);
  doc.save(`financeiro-obras-${new Date().toISOString().split('T')[0]}.pdf`);
}

function exportarCSV(dados, nomeArquivo) {
  if (!dados.length) return;
  const headers = Object.keys(dados[0]).join(';');
  const rows = dados.map(r => Object.values(r).map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(';'));
  const csv = [headers, ...rows].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = nomeArquivo; a.click();
  URL.revokeObjectURL(url);
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────

function Financeiro() {
  const [aba, setAba] = useState('geral');
  const [periodo, setPeriodo] = useState({ inicio: dataHa(12), fim: dataHoje() });
  const [temExportarCsv, setTemExportarCsv] = useState(true);

  const [resumo, setResumo] = useState(null);
  const [receitasMensais, setReceitasMensais] = useState([]);
  const [orcadoVsFechado, setOrcadoVsFechado] = useState([]);
  const [statusObras, setStatusObras] = useState([]);
  const [formasPgto, setFormasPgto] = useState([]);
  const [topClientes, setTopClientes] = useState([]);
  const [split, setSplit] = useState(null);
  const [obrasFinanceiro, setObrasFinanceiro] = useState([]);
  const [recebimentos, setRecebimentos] = useState([]);
  const [carregando, setCarregando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { inicio, fim } = periodo;
      const qs = `inicio=${inicio}&fim=${fim}`;

      const [r1, r2, r3, r4, r5, r6, r7, r8, r9] = await Promise.all([
        api.get(`/financeiro/resumo?${qs}`),
        api.get(`/financeiro/receitas-mensais?meses=12`),
        api.get(`/financeiro/orcado-vs-fechado-mensal?meses=12`),
        api.get(`/financeiro/status-obras`),
        api.get(`/financeiro/formas-pagamento?${qs}`),
        api.get(`/financeiro/top-clientes?${qs}&limit=10`),
        api.get(`/financeiro/split-servicos-materiais`),
        api.get(`/financeiro/obras-financeiro?${qs}`),
        api.get(`/financeiro/recebimentos-detalhados?${qs}`),
      ]);

      setResumo(r1.data);
      setReceitasMensais(r2.data);
      setOrcadoVsFechado(r3.data);
      setStatusObras(r4.data);
      setFormasPgto(r5.data);
      setTopClientes(r6.data);
      setSplit(r7.data);
      setObrasFinanceiro(r8.data);
      setRecebimentos(r9.data);
    } catch { }
    setCarregando(false);
  }, [periodo]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    api.get('/tenant/status').then(r => {
      setTemExportarCsv(r.data.funcionalidades?.exportar_csv ?? true);
    }).catch(() => {});
  }, []);

  const periodoStr = `${new Date(periodo.inicio + 'T12:00:00').toLocaleDateString('pt-BR')} a ${new Date(periodo.fim + 'T12:00:00').toLocaleDateString('pt-BR')}`;

  const splitData = split ? [
    { name: 'Serviços', value: split.servicos },
    { name: 'Materiais', value: split.materiais },
  ] : [];

  const abas = [
    { id: 'geral', label: '📊 Visão Geral' },
    { id: 'receitas', label: '💵 Receitas' },
    { id: 'obras', label: '🏗️ Obras & Clientes' },
    ...(temPermissao('financeiro_exportar') ? [{ id: 'relatorios', label: '📄 Relatórios' }] : []),
  ];

  return (
    <div>
      <div className="page-header">
        <h2 style={{ margin: 0 }}>💰 Financeiro</h2>
        {carregando && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Carregando...</span>}
      </div>

      {/* Filtro de período global */}
      <div className="card" style={{ padding: '14px 20px', marginBottom: 20 }}>
        <FiltroData inicio={periodo.inicio} fim={periodo.fim} onChange={setPeriodo} />
      </div>

      {/* Abas */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid var(--border-color)', paddingBottom: 0 }}>
        {abas.map(a => (
          <button key={a.id} onClick={() => setAba(a.id)} style={{
            padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 600,
            color: aba === a.id ? '#1e40af' : 'var(--text-muted)',
            borderBottom: aba === a.id ? '2px solid #1e40af' : '2px solid transparent',
            marginBottom: -2,
          }}>{a.label}</button>
        ))}
      </div>

      {/* ── ABA VISÃO GERAL ── */}
      {aba === 'geral' && resumo && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* KPIs */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <KpiCard icone="💰" titulo="Total Recebido" valor={moeda(resumo.total_recebido)} sub={periodoStr} cor="#22c55e" />
            <KpiCard icone="⏳" titulo="Total Pendente" valor={moeda(resumo.total_pendente)} sub="Saldo em aberto" cor="#ef4444" />
            <KpiCard icone="📋" titulo="Total Orçado" valor={moeda(resumo.total_orcado)} sub="Portfólio completo" cor="#3b82f6" />
            <KpiCard icone="🤝" titulo="Total Fechado" valor={moeda(resumo.total_fechado)} sub="Contratos assinados" cor="#8b5cf6" />
            <KpiCard icone="🏗️" titulo="Obras Ativas" valor={resumo.obras_ativas} sub="Em andamento" cor="#f59e0b" />
            <KpiCard icone="📈" titulo="Ticket Médio" valor={moeda(resumo.ticket_medio)} sub="Por obra fechada" cor="#06b6d4" />
            <KpiCard icone="✅" titulo="Taxa de Fechamento" valor={pct(resumo.taxa_fechamento)} sub="Orçamentos → obras" cor="#10b981" />
          </div>

          {/* Gráficos linha 1 */}
          <div className="grid-responsivo" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
            <GraficoCard
              titulo="Receita: Recebido vs Pendente por Mês"
              info="Compara quanto foi efetivamente recebido (verde) com o valor ainda em aberto (vermelho) para cada mês. Ajuda a identificar meses com concentração de inadimplência ou de recebimento."
              altura={280}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={receitasMensais} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="mes_label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<TooltipMoeda />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="recebido" name="Recebido" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="pendente" name="Pendente" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </GraficoCard>

            <GraficoCard
              titulo="Obras por Status"
              info="Distribuição percentual das obras cadastradas por status atual. Permite ver rapidamente quantas estão em orçamento, andamento, atrasadas ou finalizadas."
              altura={280}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusObras} dataKey="quantidade" nameKey="status"
                    cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}>
                    {statusObras.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, n]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </GraficoCard>
          </div>

          {/* Gráfico linha 2 */}
          <GraficoCard
            titulo="Orçado vs Fechado por Mês (por prazo combinado da obra)"
            info="Compara o valor total orçado (serviços + materiais) com o valor efetivamente fechado em contrato, agrupado pelo mês do prazo combinado de cada obra. Uma diferença consistente indica descontos frequentes ou margem de negociação."
            altura={260}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={orcadoVsFechado} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes_label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `R$${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<TooltipMoeda />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="total_orcado" name="Orçado" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="total_fechado" name="Fechado" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </GraficoCard>
        </div>
      )}

      {/* ── ABA RECEITAS ── */}
      {aba === 'receitas' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="grid-responsivo" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <GraficoCard
              titulo="Recebimentos por Mês"
              info="Evolução mensal dos valores efetivamente recebidos. Útil para identificar sazonalidade, picos de caixa e meses fracos."
              altura={260}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={receitasMensais} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="mes_label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<TooltipMoeda />} />
                  <Bar dataKey="recebido" name="Recebido" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </GraficoCard>

            <GraficoCard
              titulo="Formas de Pagamento (por volume recebido)"
              info="Distribuição do valor total recebido por forma de pagamento no período. Mostra quais métodos concentram mais receita."
              altura={260}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={formasPgto} dataKey="total" nameKey="forma"
                    cx="50%" cy="50%" outerRadius={85}
                    label={({ name, percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                    labelLine={false}>
                    {formasPgto.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
                  </Pie>
                  <Tooltip content={<TooltipPie />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </GraficoCard>
          </div>

          <GraficoCard
            titulo="Recebimento Acumulado no Tempo"
            info="Linha de crescimento do caixa acumulado ao longo dos meses. Permite visualizar o ritmo de entrada de receita e projetar tendências."
            altura={240}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={(() => {
                let acc = 0;
                return receitasMensais.map(r => { acc += r.recebido; return { ...r, acumulado: acc }; });
              })()} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes_label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `R$${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<TooltipMoeda />} />
                <Line dataKey="acumulado" name="Acumulado" stroke="#1e40af" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </GraficoCard>

          {/* Tabela de recebimentos */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h4 style={{ margin: 0 }}>Recebimentos Detalhados</h4>
                <TooltipInfo texto="Lista completa de todos os recebimentos registrados no período selecionado, com obra, cliente, forma de pagamento e valor." />
              </div>
              {temPermissao('financeiro_exportar') && (
                <button className="btn-secondary" style={{ fontSize: 12 }}
                  onClick={() => exportarExtratoPDF(recebimentos, periodoStr)}>
                  📄 Exportar PDF
                </button>
              )}
            </div>
            {recebimentos.length === 0 ? (
              <p style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: 13 }}>Nenhum recebimento no período.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Data</th><th>Obra</th><th>Cliente</th><th>Forma</th><th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {recebimentos.map(r => (
                    <tr key={r.recebimentoid}>
                      <td data-label="Data">{data(r.data_recebimento)}</td>
                      <td data-label="Obra">{r.nome_obra}</td>
                      <td data-label="Cliente">{r.nome_cliente}</td>
                      <td data-label="Forma">{r.forma_pagamento}</td>
                      <td data-label="Valor"><strong style={{ color: '#15803d' }}>{moeda(r.valor_recebido)}</strong></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'var(--bg-card)', borderTop: '2px solid var(--border-color)' }}>
                    <td colSpan={4}><strong>Total</strong></td>
                    <td data-label="Total"><strong>{moeda(recebimentos.reduce((a, r) => a + parseFloat(r.valor_recebido || 0), 0))}</strong></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── ABA OBRAS & CLIENTES ── */}
      {aba === 'obras' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="grid-responsivo" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <GraficoCard
              titulo="Top 10 Clientes por Valor Fechado"
              info="Ranking dos clientes com maior volume de contratos fechados. Ajuda a identificar os clientes mais estratégicos para o negócio."
              altura={300}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topClientes} layout="vertical" margin={{ top: 4, right: 60, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `R$${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="cliente" tick={{ fontSize: 10 }} width={80} />
                  <Tooltip content={<TooltipMoeda />} />
                  <Bar dataKey="total_fechado" name="Valor Fechado" fill="#1e40af" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </GraficoCard>

            <GraficoCard
              titulo="Split: Serviços vs Materiais (portfólio total)"
              info="Proporção entre o valor total orçado em serviços e o valor total em materiais em todo o portfólio. Indica se o negócio é mais intensivo em mão de obra ou em material."
              altura={300}
            >
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={splitData} dataKey="value" nameKey="name"
                    cx="50%" cy="50%" outerRadius={100} innerRadius={50}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
                    <Cell fill="#1e40af" /><Cell fill="#22c55e" />
                  </Pie>
                  <Tooltip content={<TooltipPie />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </GraficoCard>
          </div>

          {/* Tabela obras */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h4 style={{ margin: 0 }}>Situação Financeira por Obra</h4>
                <TooltipInfo texto="Tabela com todas as obras ativas mostrando valor orçado (serviços + materiais), valor fechado em contrato, quanto já foi recebido e saldo pendente. Ordenada por maior pendência." />
              </div>
              {temPermissao('financeiro_exportar') && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-secondary" style={{ fontSize: 12 }}
                    onClick={() => exportarObrasFinanceiroPDF(obrasFinanceiro, periodoStr)}>
                    📄 PDF
                  </button>
                  {temExportarCsv && (
                    <button className="btn-secondary" style={{ fontSize: 12 }}
                      onClick={() => exportarCSV(obrasFinanceiro.map(o => ({
                        Obra: o.nome_obra, Cliente: o.nome_cliente, Status: labelStatus(o.status_obra),
                        'Fechado (R$)': o.valor_fechado_obra, 'Serviços (R$)': o.total_servicos,
                        'Materiais (R$)': o.total_materiais, 'Recebido (R$)': o.total_recebido,
                        'Pendente (R$)': o.total_pendente,
                      })), `financeiro-obras-${periodo.inicio}-${periodo.fim}.csv`)}>
                      📊 CSV
                    </button>
                  )}
                </div>
              )}
            </div>
            <table>
              <thead>
                <tr>
                  <th>Obra</th><th>Cliente</th><th>Status</th>
                  <th>Serviços</th><th>Materiais</th><th>Fechado</th><th>Recebido</th><th>Pendente</th>
                </tr>
              </thead>
              <tbody>
                {obrasFinanceiro.map(o => (
                  <tr key={o.obraid}>
                    <td data-label="Obra"><strong>{o.nome_obra}</strong></td>
                    <td data-label="Cliente">{o.nome_cliente}</td>
                    <td data-label="Status">{labelStatus(o.status_obra)}</td>
                    <td data-label="Serviços">{moeda(o.total_servicos)}</td>
                    <td data-label="Materiais">{moeda(o.total_materiais)}</td>
                    <td data-label="Fechado">{moeda(o.valor_fechado_obra)}</td>
                    <td data-label="Recebido" style={{ color: '#15803d', fontWeight: 600 }}>{moeda(o.total_recebido)}</td>
                    <td data-label="Pendente" style={{ color: parseFloat(o.total_pendente) > 0 ? '#dc2626' : '#6b7280', fontWeight: 600 }}>
                      {moeda(o.total_pendente)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--bg-card)', borderTop: '2px solid var(--border-color)' }}>
                  <td colSpan={3}><strong>Totais</strong></td>
                  <td data-label="Serviços"><strong>{moeda(obrasFinanceiro.reduce((a, o) => a + parseFloat(o.total_servicos || 0), 0))}</strong></td>
                  <td data-label="Materiais"><strong>{moeda(obrasFinanceiro.reduce((a, o) => a + parseFloat(o.total_materiais || 0), 0))}</strong></td>
                  <td data-label="Fechado"><strong>{moeda(obrasFinanceiro.reduce((a, o) => a + parseFloat(o.valor_fechado_obra || 0), 0))}</strong></td>
                  <td data-label="Recebido"><strong style={{ color: '#15803d' }}>{moeda(obrasFinanceiro.reduce((a, o) => a + parseFloat(o.total_recebido || 0), 0))}</strong></td>
                  <td data-label="Pendente"><strong style={{ color: '#dc2626' }}>{moeda(obrasFinanceiro.reduce((a, o) => a + parseFloat(o.total_pendente || 0), 0))}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ── ABA RELATÓRIOS ── */}
      {aba === 'relatorios' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <h4 style={{ marginBottom: 4 }}>📄 Resumo Financeiro do Período</h4>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
              PDF com todos os KPIs e a tabela de receita mensal para o período selecionado no filtro acima.
            </p>
            {temPermissao('financeiro_exportar') && (
              <button className="btn-primary" onClick={() => exportarResumoFinanceiroPDF(resumo, receitasMensais, periodoStr)}>
                Exportar PDF
              </button>
            )}
          </div>

          <div className="card">
            <h4 style={{ marginBottom: 4 }}>📄 Extrato de Recebimentos</h4>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
              PDF com a lista completa de todos os recebimentos registrados no período, com obra, cliente, forma de pagamento e valor total ao final.
            </p>
            {temPermissao('financeiro_exportar') && (
              <button className="btn-primary" onClick={() => exportarExtratoPDF(recebimentos, periodoStr)}>
                Exportar PDF
              </button>
            )}
          </div>

          <div className="card">
            <h4 style={{ marginBottom: 4 }}>🏗️ Situação Financeira das Obras</h4>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
              PDF (paisagem) com todas as obras ativas e seus valores: fechado, recebido e pendente. Bom para reuniões de gestão.
            </p>
            {temPermissao('financeiro_exportar') && (
              <button className="btn-primary" onClick={() => exportarObrasFinanceiroPDF(obrasFinanceiro, periodoStr)}>
                Exportar PDF
              </button>
            )}
          </div>

          <div className="card">
            <h4 style={{ marginBottom: 4 }}>📊 Dados Completos em CSV</h4>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
              Exporta a situação financeira de todas as obras em CSV, compatível com Excel e Google Sheets para análises personalizadas.
            </p>
            {!temExportarCsv ? (
              <p style={{ fontSize: 13, color: '#f59e0b', fontWeight: 600 }}>🔒 Permitido a partir do Plano Prata</p>
            ) : temPermissao('financeiro_exportar') && (
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn-primary"
                  onClick={() => exportarCSV(obrasFinanceiro.map(o => ({
                    Obra: o.nome_obra, Cliente: o.nome_cliente, Status: labelStatus(o.status_obra),
                    'Fechado (R$)': o.valor_fechado_obra, 'Serviços (R$)': o.total_servicos,
                    'Materiais (R$)': o.total_materiais, 'Recebido (R$)': o.total_recebido,
                    'Pendente (R$)': o.total_pendente,
                  })), `financeiro-obras-${periodo.inicio}-${periodo.fim}.csv`)}>
                  Exportar Obras CSV
                </button>
                <button className="btn-primary"
                  onClick={() => exportarCSV(recebimentos.map(r => ({
                    Data: data(r.data_recebimento),
                    Obra: r.nome_obra, Cliente: r.nome_cliente,
                    'Forma de Pagamento': r.forma_pagamento,
                    'Valor (R$)': r.valor_recebido, Observação: r.observacao || '',
                  })), `financeiro-recebimentos-${periodo.inicio}-${periodo.fim}.csv`)}>
                  Exportar Recebimentos CSV
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Financeiro;
